import React, { useEffect, useRef, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    DragEndEvent,
    DragStartEvent,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    useDraggable,
    useDroppable,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Bookmark,
    ChevronsUpDown,
    Eye,
    GripVertical,
    ImageIcon,
    Loader2,
    Monitor,
    Plus,
    Settings,
    Smartphone,
    Unlink,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { setProductCardFields } from '../../../redux/vendor/settings/settingsActions';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';
import { useProductsApi } from '../../../common/hooks/use-products-api.hook';
import { useAutosave } from '../../../common/hooks/use-autosave.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { CanvasPortal } from './studio-canvas';
import { SettingsGroup, SettingsToggleRow } from '../../../components/design-system/common/SettingsPanel';

// Diagonal hatching that reads as "image placeholder" — token-driven so it
// follows the theme without hardcoding a colour.
const HATCH_STYLE: React.CSSProperties = {
    backgroundImage:
        'repeating-linear-gradient(-45deg, color-mix(in srgb, var(--foreground) 4%, transparent) 0px, color-mix(in srgb, var(--foreground) 4%, transparent) 1px, transparent 1px, transparent 8px)',
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface FieldDef {
    key: string;
    label: string;
}

interface CardItemConfig {
    key: string;
    style?: string;
    overlay?: string;
    concatWithPrevious?: boolean;
    separator?: string;
    badgeBgColor?: string;
    badgeTextColor?: string;
}

// ── Field definitions ─────────────────────────────────────────────────────────

const BUILT_IN_FIELDS: FieldDef[] = [
    { key: 'name', label: 'Name' },
    { key: 'brand', label: 'Brand' },
    { key: 'color', label: 'Color / Variant' },
    { key: 'size', label: 'Size' },
    { key: 'sku', label: 'SKU / Reference' },
    { key: 'description', label: 'Description' },
];

const DEFAULT_CARD_KEYS = ['name', 'brand', 'color', 'size'];

const STYLE_OPTIONS: { value: string; label: string }[] = [
    { value: 'default', label: 'Text' },
    { value: 'bold', label: 'Bold' },
    { value: 'muted', label: 'Muted' },
    { value: 'tag', label: 'Tag' },
    { value: 'tag-outline', label: 'Tag ○' },
];

// ── Style picker ──────────────────────────────────────────────────────────────

const StylePicker = ({ value, onChange }: {
    value: string | undefined;
    onChange: (v: string) => void;
}) => {
    const active = value ?? 'default';
    return (
        <div className="flex flex-wrap gap-1.5">
            {STYLE_OPTIONS.map(opt => {
                const isActive = active === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={cn(
                            'flex min-w-[42px] cursor-pointer items-center justify-center rounded-md px-2 py-1 transition-colors',
                            isActive
                                ? 'border-[1.5px] border-foreground bg-foreground/5'
                                : 'border-[1.5px] border-border bg-transparent hover:border-muted-foreground hover:bg-foreground/[0.04]',
                        )}
                    >
                        {opt.value === 'tag' && (
                            <span className="bg-foreground/15 px-1.5 py-px text-[10px] font-medium leading-snug text-foreground">Tag</span>
                        )}
                        {opt.value === 'tag-outline' && (
                            <span className="border border-foreground/35 px-1.5 py-px text-[10px] font-medium leading-snug text-foreground">Tag</span>
                        )}
                        {opt.value === 'default' && (
                            <span className="text-[11px] leading-snug text-muted-foreground">Aa</span>
                        )}
                        {opt.value === 'bold' && (
                            <span className="text-[11px] font-medium leading-snug text-foreground">Aa</span>
                        )}
                        {opt.value === 'muted' && (
                            <span className="text-[11px] leading-snug text-foreground/30">Aa</span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

// ── Overlay picker ────────────────────────────────────────────────────────────

const OverlayPicker = ({ value, onChange }: {
    value: string | undefined;
    onChange: (v: string) => void;
}) => {
    const active = value ?? 'none';
    const corners = [
        { pos: 'top-left', className: 'top-1.5 start-1.5' },
        { pos: 'top-right', className: 'top-1.5 end-1.5' },
        { pos: 'bottom-left', className: 'bottom-1.5 start-1.5' },
        { pos: 'bottom-right', className: 'bottom-1.5 end-1.5' },
    ];
    return (
        <div className="relative h-20 w-full overflow-hidden rounded-md border bg-foreground/[0.03]">
            <div className="pointer-events-none absolute inset-0" style={HATCH_STYLE} />

            {/* Center: None */}
            <button
                type="button"
                onClick={() => onChange('none')}
                className={cn(
                    'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border-[1.5px] px-2 py-0.5 transition-colors hover:border-muted-foreground',
                    active === 'none' ? 'border-foreground bg-foreground/10' : 'border-foreground/20 bg-transparent',
                )}
            >
                <span className={cn('select-none text-[10px] text-muted-foreground', active === 'none' && 'font-medium')}>None</span>
            </button>

            {/* Corners */}
            {corners.map(({ pos, className }) => {
                const isActive = active === pos;
                return (
                    <button
                        key={pos}
                        type="button"
                        onClick={() => onChange(pos)}
                        className={cn(
                            'absolute border-[1.5px] px-1.5 py-0.5 transition-colors hover:border-muted-foreground hover:bg-foreground/10',
                            className,
                            isActive ? 'border-foreground bg-foreground/15' : 'border-foreground/25 bg-foreground/5',
                        )}
                    >
                        <span className={cn('block h-1.5 w-3.5', isActive ? 'bg-foreground' : 'bg-foreground/25')} />
                    </button>
                );
            })}
        </div>
    );
};

// ── Field settings popover ────────────────────────────────────────────────────

const FieldSettingsPopover = ({ anchorEl, onClose, config, onUpdate }: {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    config: CardItemConfig;
    onUpdate: (updates: Partial<CardItemConfig>) => void;
}) => {
    const { t } = useTranslation();
    const open = Boolean(anchorEl);
    // Anchored to whichever gear icon opened it — the row and the on-thumbnail
    // badge both use this popover, so it positions off a virtual ref.
    const virtualRef = useRef<HTMLElement | null>(null);
    virtualRef.current = anchorEl;

    return (
        <Popover open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <PopoverAnchor virtualRef={virtualRef as any} />
            <PopoverContent side="right" align="center" className="w-64 gap-0 p-4">
                <div className="flex flex-col gap-4">

                    {/* Style */}
                    <div>
                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                            {t('PAGES.PRODUCT_DISPLAY.STYLE_LABEL')}
                        </p>
                        <StylePicker value={config.style} onChange={v => onUpdate({ style: v })} />
                    </div>

                    {/* Badge colors — for tag / tag-outline styles and overlay thumbnail badges.
                        The stored colours are the vendor's own, so swatches stay inline. */}
                    {(config.style === 'tag' || config.style === 'tag-outline' || (config.overlay && config.overlay !== 'none')) && (
                        <>
                            <Separator />
                            <div>
                                <p className="mb-2 text-xs font-medium text-muted-foreground">
                                    {t('PAGES.PRODUCT_DISPLAY.BADGE_COLORS')}
                                </p>
                                <div className="flex gap-4">
                                    {/* Fill / border color */}
                                    <div className="flex-1">
                                        <p className="mb-1 text-xs text-muted-foreground">
                                            {t('PAGES.PRODUCT_DISPLAY.BADGE_BG_COLOR')}
                                        </p>
                                        <div className="flex items-center gap-1.5">
                                            <label className="flex cursor-pointer items-center">
                                                <span
                                                    className="size-[22px] shrink-0 overflow-hidden border border-input"
                                                    style={{ backgroundColor: config.badgeBgColor ?? 'transparent' }}
                                                >
                                                    <input type="color" value={config.badgeBgColor ?? '#000000'} onChange={e => onUpdate({ badgeBgColor: e.target.value })} style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer', border: 'none', padding: 0 }} />
                                                </span>
                                            </label>
                                            <span className="text-[11px] text-muted-foreground">
                                                {config.badgeBgColor ?? '—'}
                                            </span>
                                        </div>
                                    </div>
                                    {/* Text color */}
                                    <div className="flex-1">
                                        <p className="mb-1 text-xs text-muted-foreground">
                                            {t('PAGES.PRODUCT_DISPLAY.BADGE_TEXT_COLOR')}
                                        </p>
                                        <div className="flex items-center gap-1.5">
                                            <label className="flex cursor-pointer items-center">
                                                <span
                                                    className="size-[22px] shrink-0 overflow-hidden border border-input"
                                                    style={{ backgroundColor: config.badgeTextColor ?? 'transparent' }}
                                                >
                                                    <input type="color" value={config.badgeTextColor ?? '#ffffff'} onChange={e => onUpdate({ badgeTextColor: e.target.value })} style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer', border: 'none', padding: 0 }} />
                                                </span>
                                            </label>
                                            <span className="text-[11px] text-muted-foreground">
                                                {config.badgeTextColor ?? '—'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <Separator />

                    {/* Overlay */}
                    <div>
                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                            {t('PAGES.PRODUCT_DISPLAY.POSITION_ON_THUMBNAIL')}
                        </p>
                        <OverlayPicker value={config.overlay} onChange={v => onUpdate({ overlay: v })} />
                        <p className="mt-1.5 text-xs text-muted-foreground">
                            {t('PAGES.PRODUCT_DISPLAY.OVERLAY_HINT')}
                        </p>
                    </div>

                </div>
            </PopoverContent>
        </Popover>
    );
};

// ── Card field row preview (in card mock) ──────────────────────────────────────

const CardFieldPreview = ({ config, def }: {
    config: CardItemConfig;
    def: FieldDef;
}) => {
    const { t } = useTranslation();
    const STYLE_BADGE: Record<string, string> = {
        bold: t('PAGES.PRODUCT_DISPLAY.STYLE_BOLD'),
        muted: t('PAGES.PRODUCT_DISPLAY.STYLE_MUTED'),
        tag: t('PAGES.PRODUCT_DISPLAY.STYLE_TAG'),
        'tag-outline': t('PAGES.PRODUCT_DISPLAY.STYLE_TAG_OUTLINE'),
    };
    const style = config.style ?? (config.key === 'name' ? 'bold' : 'default');
    const badge = style !== 'default' ? STYLE_BADGE[style] : null;
    return (
        <div className="flex items-center gap-1.5 overflow-hidden">
            <span className="select-none truncate text-xs font-medium leading-tight text-foreground">
                {def.label}
            </span>
            {badge && (
                <span className="shrink-0 border border-foreground/10 bg-foreground/[0.07] px-1 py-px">
                    <span className="select-none text-[9px] leading-snug text-muted-foreground">
                        {badge}
                    </span>
                </span>
            )}
        </div>
    );
};

// ── Card field row (sortable, in card mock) ───────────────────────────────────

const CardFieldRow = ({ config, def, onRemove, onUpdate }: {
    config: CardItemConfig;
    def: FieldDef;
    onRemove: (key: string) => void;
    onUpdate: (key: string, updates: Partial<CardItemConfig>) => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: config.key });
    const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
    const hasOverlay = config.overlay && config.overlay !== 'none';

    return (
        <>
            <div
                ref={setNodeRef}
                style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 1000 : 1 }}
                className={cn(
                    'group/row flex items-center gap-1.5 rounded-md border px-1.5 py-1.5 transition-colors',
                    isDragging ? 'border-foreground/20 bg-foreground/[0.07] shadow-sm' : 'border-border bg-background',
                )}
            >
                {/* Drag handle */}
                <span
                    {...attributes}
                    {...listeners}
                    className={cn('flex shrink-0 items-center text-foreground/30', isDragging ? 'cursor-grabbing' : 'cursor-grab')}
                >
                    <GripVertical className="size-3.5" />
                </span>

                {/* Style preview */}
                <span className="flex flex-1 items-center gap-1 overflow-hidden">
                    <CardFieldPreview config={config} def={def} />
                </span>

                {/* Indicators */}
                {hasOverlay && (
                    <span
                        title={`On thumbnail: ${config.overlay}`}
                        className="flex shrink-0 items-center opacity-40 transition-opacity group-hover/row:opacity-100"
                    >
                        <span className="select-none border px-1 py-px text-[9px] leading-snug text-muted-foreground">
                            {config.overlay === 'top-left' ? '↖' : config.overlay === 'top-right' ? '↗' : config.overlay === 'bottom-left' ? '↙' : '↘'}
                        </span>
                    </span>
                )}

                {/* Settings button */}
                <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setSettingsAnchor(e.currentTarget); }}
                    className="shrink-0 p-0.5 text-foreground/30 transition-colors hover:bg-foreground/[0.08] hover:text-foreground"
                >
                    <Settings className="size-[13px]" />
                </button>

                {/* Remove button */}
                <button
                    type="button"
                    onClick={() => onRemove(config.key)}
                    className="shrink-0 p-px text-foreground/25 transition-colors hover:bg-foreground/[0.08] hover:text-foreground"
                >
                    <X className="size-3" />
                </button>
            </div>

            <FieldSettingsPopover
                anchorEl={settingsAnchor}
                onClose={() => setSettingsAnchor(null)}
                config={config}
                onUpdate={updates => onUpdate(config.key, updates)}
            />
        </>
    );
};

// ── Card drop zone ────────────────────────────────────────────────────────────

const CardDropZone = ({ children, isEmpty, isActiveDrag }: {
    children: React.ReactNode;
    isEmpty: boolean;
    isActiveDrag: boolean;
}) => {
    const { t } = useTranslation();
    const { setNodeRef, isOver } = useDroppable({ id: 'card-drop-zone' });
    return (
        <div
            ref={setNodeRef}
            className={cn(
                'flex min-h-11 flex-col rounded-md border-[1.5px] border-dashed transition-colors',
                isOver ? 'border-foreground/45 bg-foreground/[0.04]'
                    : (isEmpty && isActiveDrag) ? 'border-foreground/20'
                    : isEmpty ? 'border-foreground/10'
                    : 'border-transparent',
                isEmpty ? 'items-center justify-center p-1' : 'items-stretch justify-start p-0',
            )}
        >
            {isEmpty && (
                <span className={cn('select-none text-xs italic transition-colors', isActiveDrag ? 'text-foreground/40' : 'text-foreground/20')}>
                    {isActiveDrag ? t('PAGES.PRODUCT_DISPLAY.DROP_HERE') : t('PAGES.PRODUCT_DISPLAY.NO_FIELDS')}
                </span>
            )}
            {children}
        </div>
    );
};

// ── Join drop zone (horizontal, drag next to a field to join) ─────────────────

const JoinDropZone = ({ forKey }: { forKey: string }) => {
    const { t } = useTranslation();
    const { setNodeRef, isOver } = useDroppable({ id: `join:${forKey}` });
    return (
        <div
            ref={setNodeRef}
            className={cn(
                'flex shrink-0 items-center justify-center self-stretch rounded-md border-[1.5px] border-dashed transition-all',
                isOver ? 'w-10 border-foreground/40 bg-foreground/[0.08]' : 'w-3.5 border-foreground/10 bg-foreground/[0.03]',
            )}
        >
            {isOver && (
                <span className="select-none text-[8px] text-muted-foreground" style={{ writingMode: 'vertical-lr' }}>
                    {t('PAGES.PRODUCT_DISPLAY.JOIN')}
                </span>
            )}
        </div>
    );
};

// ── Overlay corner drop zone (drag-to-position on thumbnail) ─────────────────

const CORNER_POS_CLASSES: Record<string, string> = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-right': 'bottom-0 right-0',
};

const OverlayCornerDropZone = ({ pos, isOccupied }: { pos: string; isOccupied: boolean }) => {
    const { t } = useTranslation();
    const { setNodeRef, isOver } = useDroppable({ id: `overlay:${pos}` });
    return (
        <div
            ref={setNodeRef}
            className={cn(
                'absolute z-[3] flex size-12 border-[1.5px] border-dashed p-1 transition-colors',
                CORNER_POS_CLASSES[pos],
                isOver
                    ? (isOccupied ? 'border-warning bg-warning/20' : 'border-white/85 bg-white/15')
                    : 'border-white/25 bg-black/5',
                isOver ? 'items-center justify-center'
                    : cn(pos.includes('bottom') ? 'items-end' : 'items-start', pos.includes('right') ? 'justify-end' : 'justify-start'),
            )}
        >
            {isOver && (
                <span className={cn('select-none text-[8px] font-medium leading-none', isOccupied ? 'text-warning' : 'text-white/90')}>
                    {isOccupied ? t('PAGES.PRODUCT_DISPLAY.REPLACE') : pos.replace('-', '\n')}
                </span>
            )}
        </div>
    );
};

// ── Separator connector (between joined fields) ────────────────────────────────

const SeparatorConnector = ({ item, onUpdate, onSplit }: {
    item: CardItemConfig;
    onUpdate: (key: string, updates: Partial<CardItemConfig>) => void;
    onSplit: (key: string) => void;
}) => {
    const { t } = useTranslation();
    return (
        <div className="my-0.5 flex items-center gap-1 px-2.5">
            <span className="h-px flex-1 bg-foreground/10" />
            <input
                value={item.separator ?? ', '}
                onChange={(e) => onUpdate(item.key, { separator: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                className="w-[34px] border bg-muted px-1 py-px text-center text-[10px] text-muted-foreground outline-none focus:border-foreground/40"
            />
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        className="size-5 text-foreground/30 hover:text-foreground"
                        aria-label={t('PAGES.PRODUCT_DISPLAY.SPLIT') as string}
                        onClick={() => onSplit(item.key)}
                    >
                        <Unlink className="size-[11px]" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t('PAGES.PRODUCT_DISPLAY.SPLIT')}</TooltipContent>
            </Tooltip>
            <span className="h-px flex-1 bg-foreground/10" />
        </div>
    );
};

// ── Pool chip ─────────────────────────────────────────────────────────────────

const PoolChip = ({ field, onAdd }: {
    field: FieldDef;
    onAdd: (key: string) => void;
}) => {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: field.key });
    return (
        <div
            ref={setNodeRef}
            {...attributes}
            className={cn(
                'inline-flex select-none items-center gap-1 rounded-md border bg-background py-0.5 pe-0.5 ps-1 transition-colors hover:border-foreground/35 hover:bg-muted/50',
                isDragging && 'opacity-30',
            )}
        >
            <span {...listeners} className={cn('flex shrink-0 items-center text-foreground/30', isDragging ? 'cursor-grabbing' : 'cursor-grab')}>
                <GripVertical className="size-3.5" />
            </span>
            <span className="text-xs font-medium text-foreground">
                {field.label}
            </span>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        className="size-5 text-foreground/30 hover:text-foreground"
                        aria-label={t('PAGES.PRODUCT_DISPLAY.ADD_TO_CARD') as string}
                        onClick={() => onAdd(field.key)}
                    >
                        <Plus className="size-3.5" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t('PAGES.PRODUCT_DISPLAY.ADD_TO_CARD')}</TooltipContent>
            </Tooltip>
        </div>
    );
};

// ── Drag overlay ──────────────────────────────────────────────────────────────

const OverlayPill = ({ label }: { label: string }) => (
    <span className="inline-flex cursor-grabbing items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 shadow-lg">
        <GripVertical className="size-4 text-muted-foreground" />
        <span className="text-xs font-medium text-foreground">{label}</span>
    </span>
);

// ── Product image placeholder (with interactive overlay badge controls) ────────

const OVERLAY_POS: Record<string, React.CSSProperties> = {
    'top-left': { top: 5, left: 5 },
    'top-right': { top: 5, right: 5 },
    'bottom-left': { bottom: 5, left: 5 },
    'bottom-right': { bottom: 5, right: 5 },
};

const OVERLAY_CORNERS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const;

// ── Draggable overlay badge ────────────────────────────────────────────────────

const DraggableOverlayBadge = ({ item, def, onRemove, onSettings }: {
    item: CardItemConfig;
    def: FieldDef;
    onRemove: (key: string) => void;
    onSettings: (key: string, el: HTMLElement) => void;
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: item.key });

    return (
        <div
            ref={setNodeRef}
            className="absolute"
            style={{ ...OVERLAY_POS[item.overlay!], zIndex: isDragging ? 0 : 4, opacity: isDragging ? 0 : 1 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Mimics the storefront's default badge treatment: dark glass chip, light text */}
            <div className="flex items-center gap-px bg-black/60 px-1 py-0.5 backdrop-blur-sm">
                {/* Drag handle */}
                <span
                    {...attributes}
                    {...listeners}
                    className="me-px flex cursor-grab items-center text-white/45 active:cursor-grabbing"
                >
                    <GripVertical className="size-[9px]" />
                </span>
                <span className="select-none whitespace-nowrap text-[8px] font-medium leading-snug text-white/90">
                    {def.label}
                </span>
                <button
                    type="button"
                    onClick={e => { e.stopPropagation(); onSettings(item.key, e.currentTarget); }}
                    className="p-px text-white/65 hover:text-white"
                >
                    <Settings className="size-[9px]" />
                </button>
                {isHovered && (
                    <button
                        type="button"
                        onClick={() => onRemove(item.key)}
                        className="p-px text-white/65 hover:text-white"
                    >
                        <X className="size-[9px]" />
                    </button>
                )}
            </div>
        </div>
    );
};

const ProductImagePlaceholder = ({ cardItems, allFields, onUpdate, onRemove, activeId }: {
    cardItems: CardItemConfig[];
    allFields: FieldDef[];
    onUpdate: (key: string, updates: Partial<CardItemConfig>) => void;
    onRemove: (key: string) => void;
    activeId: string | null;
}) => {
    const [settingsState, setSettingsState] = useState<{ key: string; anchorEl: HTMLElement } | null>(null);

    const overlayItems = cardItems.filter(f => f.overlay && f.overlay !== 'none');
    const occupiedPositions = new Set(overlayItems.map(f => f.overlay!));
    const settingsItem = settingsState ? cardItems.find(f => f.key === settingsState.key) : null;

    // Don't show a corner zone for the position the dragged item already occupies
    const activeItemOverlay = activeId ? cardItems.find(f => f.key === activeId)?.overlay : undefined;

    return (
        <div className="relative w-full overflow-hidden bg-foreground/5 pb-[100%]">
            <div className="absolute inset-0" style={HATCH_STYLE} />
            <div className="absolute inset-0 flex items-center justify-center">
                <ImageIcon className="size-8 text-foreground/10" />
            </div>

            {/* Corner drop zones — visible while dragging */}
            {activeId !== null && OVERLAY_CORNERS.map(pos => {
                if (activeItemOverlay === pos) return null;
                return (
                    <OverlayCornerDropZone
                        key={pos}
                        pos={pos}
                        isOccupied={occupiedPositions.has(pos)}
                    />
                );
            })}

            {overlayItems.map(item => {
                const def = allFields.find(f => f.key === item.key);
                if (!def) return null;
                return (
                    <DraggableOverlayBadge
                        key={item.key}
                        item={item}
                        def={def}
                        onRemove={onRemove}
                        onSettings={(key, el) => setSettingsState({ key, anchorEl: el })}
                    />
                );
            })}
            {settingsItem && (
                <FieldSettingsPopover
                    anchorEl={settingsState!.anchorEl}
                    onClose={() => setSettingsState(null)}
                    config={settingsItem}
                    onUpdate={updates => onUpdate(settingsItem.key, updates)}
                />
            )}
        </div>
    );
};

// ── Live preview helpers ───────────────────────────────────────────────────────

const getProductFieldValue = (product: any, key: string): string | undefined => {
    if (key === 'name') return product.name;
    if (key === 'brand') return product.brand;
    if (key === 'color') return product.color;
    if (key === 'size') return product.sizeName;
    if (key === 'sku') return product.sku;
    if (key === 'description') return product.description;
    if (key.startsWith('attr:')) return product.attributes?.[key.replace('attr:', '')];
    return undefined;
};

const POS_STYLE: Record<string, React.CSSProperties> = {
    'top-left': { top: 4, left: 4 },
    'top-right': { top: 4, right: 4 },
    'bottom-left': { bottom: 4, left: 4 },
    'bottom-right': { bottom: 4, right: 4 },
};

const RealProductCard = ({ product, cardItems }: {
    product: any;
    cardItems: CardItemConfig[];
}) => {
    const textItems = cardItems.filter(f => !f.overlay || f.overlay === 'none');
    const overlayItems = cardItems.filter(f => f.overlay && f.overlay !== 'none');

    // Group text items by concatWithPrevious
    const rows: CardItemConfig[][] = [];
    textItems.forEach(item => {
        if (item.concatWithPrevious && rows.length > 0) {
            rows[rows.length - 1].push(item);
        } else {
            rows.push([item]);
        }
    });

    const renderRow = (row: CardItemConfig[]) => {
        const style = row[0].style ?? (row[0].key === 'name' ? 'bold' : 'default');
        const text = row.map((item, i) => {
            const val = getProductFieldValue(product, item.key) ?? '';
            return i === 0 ? val : `${item.separator ?? ', '}${val}`;
        }).join('');
        if (!text) return null;

        if (style === 'tag') {
            return (
                <span
                    className={cn('mb-0.5 inline-flex px-1 py-px', !row[0].badgeBgColor && 'bg-foreground/10')}
                    style={row[0].badgeBgColor ? { backgroundColor: row[0].badgeBgColor } : undefined}
                >
                    <span
                        className={cn('text-[0.55rem] font-medium leading-snug', !row[0].badgeTextColor && 'text-foreground')}
                        style={row[0].badgeTextColor ? { color: row[0].badgeTextColor } : undefined}
                    >{text}</span>
                </span>
            );
        }
        if (style === 'tag-outline') {
            return (
                <span
                    className={cn('mb-0.5 inline-flex border px-1 py-px', !row[0].badgeBgColor && 'border-foreground/30')}
                    style={row[0].badgeBgColor ? { borderColor: row[0].badgeBgColor } : undefined}
                >
                    <span
                        className={cn('text-[0.55rem] font-medium leading-snug', !row[0].badgeTextColor && 'text-foreground')}
                        style={row[0].badgeTextColor ? { color: row[0].badgeTextColor } : undefined}
                    >{text}</span>
                </span>
            );
        }
        return (
            <span className={cn(
                'mb-0.5 block truncate leading-tight',
                style === 'bold' ? 'text-[0.6rem] font-medium' : 'text-[0.55rem]',
                style === 'muted' ? 'text-foreground/35' : 'text-muted-foreground',
            )}>{text}</span>
        );
    };

    return (
        <>
            {/* Thumbnail */}
            <div className="relative w-full overflow-hidden bg-foreground/5 pb-[100%]">
                {product.thumbnailPath ? (
                    <img src={product.thumbnailPath} alt={product.name}
                        className="absolute inset-0 size-full object-cover" />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <ImageIcon className="size-8 text-foreground/10" />
                    </div>
                )}
                {overlayItems.map(item => {
                    const val = getProductFieldValue(product, item.key);
                    if (!val) return null;
                    return (
                        <span key={item.key} className="absolute" style={POS_STYLE[item.overlay!]}>
                            <span
                                className="flex items-center px-1 py-0.5 backdrop-blur-sm"
                                style={{ backgroundColor: item.badgeBgColor ?? 'rgba(0,0,0,0.55)' }}
                            >
                                <span
                                    className="whitespace-nowrap text-[0.5rem] font-medium leading-none"
                                    style={{ color: item.badgeTextColor ?? 'rgba(255,255,255,0.9)' }}
                                >{val}</span>
                            </span>
                        </span>
                    );
                })}
            </div>

            {/* Text fields */}
            {rows.length > 0 && (
                <div className="p-1.5">
                    {rows.map((row, i) => <div key={i}>{renderRow(row)}</div>)}
                </div>
            )}
        </>
    );
};

// ── Live mobile card preview ──────────────────────────────────────────────────

const RealMobileCard = ({ product, cardItems, showBookmark }: {
    product: any;
    cardItems: CardItemConfig[];
    showBookmark: boolean;
}) => {
    const overlayItem = cardItems.find(f => f.overlay && f.overlay !== 'none');
    const textItems = cardItems.filter(f => !f.overlay || f.overlay === 'none');

    // Group text items by concatWithPrevious
    const rows: CardItemConfig[][] = [];
    textItems.forEach(item => {
        if (item.concatWithPrevious && rows.length > 0) {
            rows[rows.length - 1].push(item);
        } else {
            rows.push([item]);
        }
    });

    return (
        <div className="flex w-full flex-col items-center gap-3">
            {/* Horizontal scroll mockup */}
            <div className="flex items-start justify-center gap-2">
                {/* Ghost card left */}
                <div className="size-20 shrink-0 rounded-md border bg-background opacity-50" />

                {/* Active card - thumbnail only */}
                <div className="relative size-[140px] shrink-0 overflow-hidden rounded-md border bg-background shadow-sm">
                    {product.thumbnailPath ? (
                        <img src={product.thumbnailPath} alt={product.name} className="size-full object-cover" />
                    ) : (
                        <div className="flex size-full items-center justify-center bg-foreground/5">
                            <ImageIcon className="size-6 text-foreground/10" />
                        </div>
                    )}

                    {/* Bookmark */}
                    {showBookmark && (
                        <span className="absolute end-[3px] top-[3px] z-[5]">
                            <Bookmark className="size-3 text-foreground/35" strokeWidth={2} />
                        </span>
                    )}

                    {/* Overlay badge */}
                    {overlayItem && (() => {
                        const val = getProductFieldValue(product, overlayItem.key);
                        if (!val) return null;
                        const posStyle = POS_STYLE[overlayItem.overlay!] ?? POS_STYLE['top-left'];
                        return (
                            <span className="absolute z-[4]" style={posStyle}>
                                <span
                                    className="block px-1 py-px backdrop-blur-sm"
                                    style={{ backgroundColor: overlayItem.badgeBgColor ?? 'rgba(0,0,0,0.55)' }}
                                >
                                    <span
                                        className="whitespace-nowrap text-[7px] font-medium leading-none"
                                        style={{ color: overlayItem.badgeTextColor ?? 'rgba(255,255,255,0.9)' }}
                                    >{val}</span>
                                </span>
                            </span>
                        );
                    })()}
                </div>

                {/* Ghost card right */}
                <div className="size-20 shrink-0 rounded-md border bg-background opacity-50" />
            </div>

            {/* Info bar preview - mimics the mobile drawer info bar */}
            {rows.length > 0 && (
                <div className="w-full max-w-[320px] rounded-lg border bg-background px-4 py-2">
                    {rows.map((row, i) => {
                        const style = row[0].style ?? (row[0].key === 'name' ? 'bold' : 'default');
                        const text = row.map((item, j) => {
                            const val = getProductFieldValue(product, item.key) ?? '';
                            return j === 0 ? val : `${item.separator ?? ', '}${val}`;
                        }).join('');
                        if (!text) return null;

                        if (style === 'tag' || style === 'tag-outline') {
                            return (
                                <span
                                    key={i}
                                    className={cn(
                                        'mb-0.5 me-1 inline-flex px-1 py-px',
                                        style === 'tag' && !row[0].badgeBgColor && 'bg-foreground/10',
                                        style === 'tag-outline' && cn('border', !row[0].badgeBgColor && 'border-foreground/30'),
                                    )}
                                    style={row[0].badgeBgColor ? (style === 'tag' ? { backgroundColor: row[0].badgeBgColor } : { borderColor: row[0].badgeBgColor }) : undefined}
                                >
                                    <span
                                        className={cn('text-[0.55rem] font-medium leading-snug', !row[0].badgeTextColor && 'text-foreground')}
                                        style={row[0].badgeTextColor ? { color: row[0].badgeTextColor } : undefined}
                                    >{text}</span>
                                </span>
                            );
                        }
                        return (
                            <span key={i} className={cn(
                                'block truncate leading-tight',
                                style === 'bold' ? 'text-[0.6rem] font-medium' : 'text-[0.55rem]',
                                style === 'muted' ? 'text-foreground/35' : style === 'bold' ? 'text-foreground' : 'text-muted-foreground',
                            )}>{text}</span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────

// Rendered only inside the Visualizer studio's settings column; `inTab` is kept
// so the call site keeps compiling, but there is no standalone layout any more.
export const ProductDisplaySettings = (_props: { inTab?: boolean }) => {
    const settingsAPI = useSettingsApi();
    const productsAPI = useProductsApi();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [cardItems, setCardItems] = useState<CardItemConfig[]>(
        DEFAULT_CARD_KEYS.map(k => ({ key: k }))
    );

    // Mirror cardItems into Redux so the live preview reflects each change
    useEffect(() => {
        dispatch(setProductCardFields(cardItems));
    }, [cardItems, dispatch]);
    const [mobileCardItems, setMobileCardItems] = useState<CardItemConfig[]>([{ key: 'name' }, { key: 'brand' }]);
    const [mobileShowBookmark, setMobileShowBookmark] = useState(true);
    const [mobileOverlaySettings, setMobileOverlaySettings] = useState<{ key: string; anchorEl: HTMLElement } | null>(null);
    const [activeDevice, setActiveDevice] = useState<'web' | 'mobile'>('web');
    const [customAttrDefs, setCustomAttrDefs] = useState<FieldDef[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /** Set once the stored config has loaded: nothing is saved before that. */
    const [loaded, setLoaded] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);

    // Live preview state
    const [previewMode, setPreviewMode] = useState<'skeleton' | 'live'>('skeleton');
    const [liveProducts, setLiveProducts] = useState<any[]>([]);
    const [liveProductsLoading, setLiveProductsLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
    const [productSearchOpen, setProductSearchOpen] = useState(false);

    // Computed active items based on device
    const activeItems = activeDevice === 'web' ? cardItems : mobileCardItems;
    const setActiveItems = activeDevice === 'web' ? setCardItems : setMobileCardItems;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const allFields: FieldDef[] = [...BUILT_IN_FIELDS, ...customAttrDefs];
    const cardFieldKeys = activeItems.map(f => f.key);
    const poolFields = allFields.filter(f => !cardFieldKeys.includes(f.key));
    const activeField = activeId ? allFields.find(f => f.key === activeId) ?? null : null;

    useEffect(() => {
        const load = async () => {
            try {
                const config = await settingsAPI.findConfig();
                const savedFields: { key: string; enabled: boolean; order: number; style?: string; overlay?: string; concatWithPrevious?: boolean; separator?: string; badgeBgColor?: string; badgeTextColor?: string }[] = config.productCardFields ?? [];
                const customDefs: { fieldName: string; fieldLabel: string }[] = config.customFields ?? [];

                setCustomAttrDefs(customDefs.map(cf => ({ key: `attr:${cf.fieldName}`, label: cf.fieldLabel })));

                if (savedFields.length) {
                    const enabled = savedFields.filter(f => f.enabled).sort((a, b) => a.order - b.order);
                    setCardItems(enabled.map(f => ({ key: f.key, style: f.style, overlay: f.overlay, concatWithPrevious: f.concatWithPrevious, separator: f.separator, badgeBgColor: f.badgeBgColor, badgeTextColor: f.badgeTextColor })));
                }

                // Load mobile config
                const mobileSavedFields: typeof savedFields = config.mobileProductCardFields ?? [];
                if (mobileSavedFields.length) {
                    const mobileEnabled = mobileSavedFields.filter(f => f.enabled).sort((a, b) => a.order - b.order);
                    setMobileCardItems(mobileEnabled.map(f => ({ key: f.key, style: f.style, overlay: f.overlay, concatWithPrevious: f.concatWithPrevious, separator: f.separator, badgeBgColor: f.badgeBgColor, badgeTextColor: f.badgeTextColor })));
                }
                if (config.mobileProductCardShowBookmark !== undefined) {
                    setMobileShowBookmark(config.mobileProductCardShowBookmark);
                }
                setLoaded(true);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (previewMode !== 'live' || liveProducts.length > 0) return;
        setLiveProductsLoading(true);
        productsAPI.findAllProducts().then((p: any[]) => {
            setLiveProducts(p);
            setLiveProductsLoading(false);
        }).catch(() => setLiveProductsLoading(false));
    }, [previewMode]);

    const handleDragStart = (event: DragStartEvent) => setActiveId(event.active.id as string);

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = event;
        if (!over) return;
        const activeKey = active.id as string;
        const overId = over.id as string;

        // Overlay corner drop → place/move field onto thumbnail at that corner
        if (overId.startsWith('overlay:')) {
            const pos = overId.replace('overlay:', '');
            const isActiveInCard = cardFieldKeys.includes(activeKey);
            const occupantKey = activeItems.find(f => f.key !== activeKey && f.overlay === pos)?.key;

            // Mobile: only one overlay badge allowed — clear all other overlays
            if (activeDevice === 'mobile') {
                if (isActiveInCard) {
                    setActiveItems(prev => prev.map(f => {
                        if (f.key === activeKey) return { ...f, overlay: pos };
                        // Clear overlay from all other fields
                        if (f.overlay && f.overlay !== 'none') return { ...f, overlay: undefined };
                        return f;
                    }));
                } else {
                    setActiveItems(prev => {
                        const cleared = prev.map(f => f.overlay && f.overlay !== 'none' ? { ...f, overlay: undefined } : f);
                        return [...cleared, { key: activeKey, overlay: pos }];
                    });
                }
                return;
            }

            if (isActiveInCard) {
                setActiveItems(prev => prev.map(f => {
                    if (f.key === activeKey) return { ...f, overlay: pos };
                    if (f.key === occupantKey) return { ...f, overlay: undefined };
                    return f;
                }));
            } else {
                // Add from pool directly as an overlay field
                setActiveItems(prev => {
                    const evicted = prev.map(f => f.key === occupantKey ? { ...f, overlay: undefined } : f);
                    return [...evicted, { key: activeKey, overlay: pos }];
                });
            }
            return;
        }

        // Join drop → join dragged field with the target field
        if (overId.startsWith('join:')) {
            const afterKey = overId.replace('join:', '');
            const withoutDragged = activeItems.filter(f => f.key !== activeKey);
            const insertAt = withoutDragged.findIndex(f => f.key === afterKey);
            if (insertAt === -1) return;
            const newItems = [...withoutDragged];
            newItems.splice(insertAt + 1, 0, { key: activeKey, concatWithPrevious: true, separator: ', ' });
            setActiveItems(newItems);
            return;
        }

        const isActiveInCard = cardFieldKeys.includes(activeKey);
        const isOverCard = cardFieldKeys.includes(overId) || overId === 'card-drop-zone';
        const activeItemConfig = activeItems.find(f => f.key === activeKey);
        const isActiveOverlay = !!(activeItemConfig?.overlay && activeItemConfig.overlay !== 'none');

        // Overlay badge dragged back to text list
        if (isActiveOverlay && isOverCard) {
            const insertAfterKey = cardFieldKeys.includes(overId) ? overId : null;
            setActiveItems(prev => {
                const withoutActive = prev.filter(f => f.key !== activeKey);
                // Mobile: auto-concat if rows are full
                const insertItem: CardItemConfig = {
                    key: activeKey,
                    ...(activeItemConfig?.style && { style: activeItemConfig.style }),
                    ...(isMobileRowsFull && { concatWithPrevious: true, separator: ', ' }),
                };
                if (insertAfterKey) {
                    const idx = withoutActive.findIndex(f => f.key === insertAfterKey);
                    const next = [...withoutActive];
                    next.splice(idx, 0, insertItem);
                    return next;
                }
                return [...withoutActive, insertItem];
            });
            return;
        }

        if (!isActiveInCard && isOverCard) {
            // Adding from pool to card — mobile: auto-concat if rows are full
            if (isMobileRowsFull) {
                setActiveItems(prev => [...prev, { key: activeKey, concatWithPrevious: true, separator: ', ' }]);
            } else {
                const insertIndex = cardFieldKeys.includes(overId) ? cardFieldKeys.indexOf(overId) : activeItems.length;
                setActiveItems(prev => { const next = [...prev]; next.splice(insertIndex, 0, { key: activeKey }); return next; });
            }
        } else if (isActiveInCard && isOverCard && activeKey !== overId && cardFieldKeys.includes(overId)) {
            const oldIndex = cardFieldKeys.indexOf(activeKey);
            const newIndex = cardFieldKeys.indexOf(overId);
            setActiveItems(prev => arrayMove(prev, oldIndex, newIndex));
        }
    };

    const handleSplitField = (key: string) => {
        // Mobile: splitting creates a new row — block if already at max
        if (isMobileRowsFull) return;
        setActiveItems(prev => prev.map(f => f.key === key ? { ...f, concatWithPrevious: false } : f));
    };

    const handleAddFromPool = (key: string) => {
        if (cardFieldKeys.includes(key)) return;
        // Mobile: auto-concatenate with last row if already at max rows
        if (isMobileRowsFull) {
            setActiveItems(prev => [...prev, { key, concatWithPrevious: true, separator: ', ' }]);
            return;
        }
        setActiveItems(prev => [...prev, { key }]);
    };

    const handleRemoveFromCard = (key: string) => {
        setActiveItems(prev => prev.filter(f => f.key !== key));
    };

    const handleUpdateField = (key: string, updates: Partial<CardItemConfig>) => {
        // Mobile: enforce single overlay constraint (only one field can have overlay)
        if (activeDevice === 'mobile' && updates.overlay && updates.overlay !== 'none') {
            setActiveItems(prev => prev.map(f => {
                if (f.key === key) return { ...f, ...updates };
                // Clear overlay from other fields
                if (f.overlay && f.overlay !== 'none') return { ...f, overlay: undefined };
                return f;
            }));
            return;
        }
        setActiveItems(prev => prev.map(f => f.key === key ? { ...f, ...updates } : f));
    };

    // What the card settings store, built the same way whether or not anything changed.
    const buildPayload = () => {
        const serializeFields = (items: CardItemConfig[]) => items.map((f, i) => ({ key: f.key, enabled: true, order: i, ...(f.style && { style: f.style }), ...(f.overlay && f.overlay !== 'none' && { overlay: f.overlay }), ...(f.concatWithPrevious !== undefined && { concatWithPrevious: f.concatWithPrevious }), ...(f.separator !== undefined && { separator: f.separator }), ...(f.badgeBgColor && { badgeBgColor: f.badgeBgColor }), ...(f.badgeTextColor && { badgeTextColor: f.badgeTextColor }) }));

        const webCardFieldKeys = cardItems.map(f => f.key);
        const enabledFields = serializeFields(cardItems);
        const disabledFields = allFields.filter(f => !webCardFieldKeys.includes(f.key)).map((f, i) => ({ key: f.key, enabled: false, order: cardItems.length + i }));

        const mobileCardFieldKeys = mobileCardItems.map(f => f.key);
        const mobileEnabledFields = serializeFields(mobileCardItems);
        const mobileDisabledFields = allFields.filter(f => !mobileCardFieldKeys.includes(f.key)).map((f, i) => ({ key: f.key, enabled: false, order: mobileCardItems.length + i }));

        return {
            productCardFields: [...enabledFields, ...disabledFields],
            mobileProductCardFields: [...mobileEnabledFields, ...mobileDisabledFields],
            mobileProductCardShowBookmark: mobileShowBookmark,
        };
    };

    // Saved a moment after the last change: no Save button, no full-screen overlay.
    const payload = buildPayload();
    useAutosave({
        data: payload,
        ready: loaded,
        save: () => settingsAPI.updateConfig(payload),
    });

    // Separate text fields (shown in sortable list) from overlay fields (shown on image)
    const textCardItems = activeItems.filter(f => !f.overlay || f.overlay === 'none');
    const textCardKeys = textCardItems.map(f => f.key);
    const cardDefs = textCardItems.map(item => allFields.find(f => f.key === item.key)).filter(Boolean) as FieldDef[];

    // Mobile: count text rows (concatenated fields share a row)
    const MOBILE_MAX_TEXT_ROWS = 2;
    const mobileTextRowCount = textCardItems.filter(f => !f.concatWithPrevious).length;
    const isMobileRowsFull = activeDevice === 'mobile' && mobileTextRowCount >= MOBILE_MAX_TEXT_ROWS;

    // Renders the shared sortable list of text fields — same list on web and mobile
    const renderFieldList = () => (
        <SortableContext items={textCardKeys} strategy={verticalListSortingStrategy}>
            <CardDropZone isEmpty={cardDefs.length === 0} isActiveDrag={activeId !== null}>
                {textCardItems.map((item) => {
                    const def = allFields.find(f => f.key === item.key);
                    if (!def) return null;
                    return (
                        <div key={item.key}>
                            {item.concatWithPrevious && (
                                <SeparatorConnector item={item} onUpdate={handleUpdateField} onSplit={handleSplitField} />
                            )}
                            <div className="mb-[3px] flex items-stretch gap-[3px]">
                                <div className="min-w-0 flex-1">
                                    <CardFieldRow
                                        config={item}
                                        def={def}
                                        onRemove={handleRemoveFromCard}
                                        onUpdate={handleUpdateField}
                                    />
                                </div>
                                {activeId !== null && activeId !== item.key && (
                                    <JoinDropZone forKey={item.key} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </CardDropZone>
        </SortableContext>
    );

    // "Select a product above" — shown in place of a card in Live mode until one is picked
    const selectProductPrompt = (
        <div className="flex flex-col items-center gap-1.5 px-2 text-center">
            <Eye className="size-5 text-muted-foreground/60" />
            <span className="text-xs text-muted-foreground">
                {t('PAGES.PRODUCT_DISPLAY.SELECT_PRODUCT')}
            </span>
        </div>
    );

    const fieldListSkeleton = (rows: number) => (
        <>
            {Array.from({ length: rows }, (_, i) => (
                <div key={i} className="mb-[3px] h-[26px] animate-pulse rounded-md bg-foreground/5" />
            ))}
        </>
    );

    return (
        <FeatureGate feature="productCardCustomization">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>

                {/* ── Settings column ── */}
                <div className="flex flex-col gap-8">

                    {/* Device — web and mobile cards are configured separately */}
                    <SettingsGroup
                        title={t('PAGES.PRODUCT_DISPLAY.DEVICE', 'Device')}
                        description={t('PAGES.PRODUCT_DISPLAY.DEVICE_HINT', 'Web and mobile cards are set up separately.')}
                    >
                        <ToggleGroup
                            type="single"
                            variant="outline"
                            size="sm"
                            value={activeDevice}
                            onValueChange={(v) => v && setActiveDevice(v as 'web' | 'mobile')}
                        >
                            <ToggleGroupItem value="web">
                                <Monitor className="size-4" />
                                {t('INTEGRATION.DEVICE.WEB')}
                            </ToggleGroupItem>
                            <ToggleGroupItem value="mobile">
                                <Smartphone className="size-4" />
                                {t('INTEGRATION.DEVICE.MOBILE')}
                            </ToggleGroupItem>
                        </ToggleGroup>

                        {/* Mobile-only card setting */}
                        {activeDevice === 'mobile' && (
                            <SettingsToggleRow
                                id="product-card-show-bookmark"
                                icon={<Bookmark />}
                                label={t('PAGES.PRODUCT_DISPLAY.SHOW_BOOKMARK')}
                                description={t('PAGES.PRODUCT_DISPLAY.SHOW_BOOKMARK_HINT')}
                                checked={mobileShowBookmark}
                                onCheckedChange={setMobileShowBookmark}
                            />
                        )}
                    </SettingsGroup>

                    {/* Available fields pool */}
                    <SettingsGroup
                        title={t('PAGES.PRODUCT_DISPLAY.AVAILABLE_FIELDS')}
                        description={t('PAGES.PRODUCT_DISPLAY.AVAILABLE_FIELDS_SUBTITLE')}
                    >
                        {isLoading ? (
                            <div className="flex flex-wrap gap-1.5">
                                {[80, 120, 60, 100, 140].map((w, i) => (
                                    <span key={i} className="h-7 animate-pulse rounded-md bg-foreground/5" style={{ width: w }} />
                                ))}
                            </div>
                        ) : poolFields.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                {t('PAGES.PRODUCT_DISPLAY.ALL_FIELDS_ON_CARD')}
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {poolFields.map(field => (
                                    <PoolChip key={field.key} field={field} onAdd={handleAddFromPool} />
                                ))}
                            </div>
                        )}
                    </SettingsGroup>
                </div>

                {/* ── Card preview — drawn on the studio's canvas ── */}
                <CanvasPortal>
                    <div className="flex flex-col gap-6">

                        {/* Toolbar */}
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <ToggleGroup
                                type="single"
                                variant="outline"
                                size="sm"
                                className="bg-background"
                                value={previewMode}
                                onValueChange={(v) => v && setPreviewMode(v as 'skeleton' | 'live')}
                            >
                                <ToggleGroupItem value="skeleton">{t('PAGES.PRODUCT_DISPLAY.PREVIEW_SKELETON')}</ToggleGroupItem>
                                <ToggleGroupItem value="live">{t('PAGES.PRODUCT_DISPLAY.PREVIEW_LIVE')}</ToggleGroupItem>
                            </ToggleGroup>

                            {/* Product search (live mode only) */}
                            {previewMode === 'live' && (
                                <div className="flex min-w-0 items-center gap-1">
                                    <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="w-64 justify-between bg-background font-normal"
                                            >
                                                <span className={cn('truncate', !selectedProduct && 'text-muted-foreground')}>
                                                    {selectedProduct
                                                        ? (selectedProduct.name ?? selectedProduct.sku ?? '')
                                                        : t('PAGES.PRODUCT_DISPLAY.SEARCH_PRODUCT')}
                                                </span>
                                                {liveProductsLoading
                                                    ? <Loader2 className="size-4 animate-spin text-muted-foreground" />
                                                    : <ChevronsUpDown className="size-4 text-muted-foreground" />}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="end">
                                            <Command>
                                                <CommandInput placeholder={t('PAGES.PRODUCT_DISPLAY.SEARCH_PRODUCT')} />
                                                <CommandList>
                                                    <CommandEmpty>{t('COMMON.NO_RESULTS', 'No results')}</CommandEmpty>
                                                    {liveProducts.map((opt, i) => (
                                                        <CommandItem
                                                            key={opt.sku ?? opt.uuid ?? i}
                                                            value={`${opt.name ?? ''} ${opt.sku ?? ''}`.trim() || String(i)}
                                                            onSelect={() => { setSelectedProduct(opt); setProductSearchOpen(false); }}
                                                            className="flex items-center gap-3 py-1.5"
                                                        >
                                                            {opt.thumbnailPath && (
                                                                <img src={opt.thumbnailPath} alt="" className="size-8 shrink-0 rounded-sm object-cover" />
                                                            )}
                                                            <span className="min-w-0">
                                                                <span className="block truncate text-xs font-medium text-foreground">{opt.name}</span>
                                                                <span className="block text-xs text-muted-foreground">{opt.sku}</span>
                                                            </span>
                                                        </CommandItem>
                                                    ))}
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                    {selectedProduct && (
                                        <Button
                                            type="button"
                                            size="icon-sm"
                                            variant="ghost"
                                            aria-label={t('COMMON.CLEAR', 'Clear')}
                                            onClick={() => setSelectedProduct(null)}
                                        >
                                            <X className="size-4" />
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>

                        {activeDevice === 'web' ? (
                            /* ── Web preview ── */
                            <div className="flex flex-col items-center gap-4 py-4">
                                <div className="flex items-start justify-center gap-4">

                                    {/* Ghost card left */}
                                    <div className="mt-6 hidden w-28 shrink-0 overflow-hidden rounded-lg border bg-background opacity-50 sm:block">
                                        <div className="w-full bg-foreground/5 pb-[100%]" />
                                        <div className="p-2">
                                            <div className="mb-1.5 h-2 w-[80%] rounded-sm bg-foreground/[0.12]" />
                                            <div className="h-1.5 w-[55%] rounded-sm bg-foreground/[0.07]" />
                                        </div>
                                    </div>

                                    {/* Active card */}
                                    <div className="w-[260px] flex-none overflow-hidden rounded-lg border bg-background shadow-sm">
                                        {previewMode === 'live' && selectedProduct ? (
                                            <RealProductCard product={selectedProduct} cardItems={activeItems} />
                                        ) : previewMode === 'live' && !selectedProduct ? (
                                            <div className="relative w-full bg-foreground/[0.04] pb-[100%]">
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    {selectProductPrompt}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <ProductImagePlaceholder cardItems={activeItems} allFields={allFields} onUpdate={handleUpdateField} onRemove={handleRemoveFromCard} activeId={activeId} />
                                                <div className="p-2">
                                                    {isLoading ? fieldListSkeleton(3) : renderFieldList()}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Ghost card right */}
                                    <div className="mt-6 hidden w-28 shrink-0 overflow-hidden rounded-lg border bg-background opacity-50 sm:block">
                                        <div className="w-full bg-foreground/5 pb-[100%]" />
                                        <div className="p-2">
                                            <div className="mb-1.5 h-2 w-[70%] rounded-sm bg-foreground/[0.12]" />
                                            <div className="h-1.5 w-[45%] rounded-sm bg-foreground/[0.07]" />
                                        </div>
                                    </div>
                                </div>

                                {previewMode === 'skeleton' && (
                                    <p className="text-center text-xs text-muted-foreground">
                                        {t('PAGES.PRODUCT_DISPLAY.CARD_PREVIEW_SUBTITLE')}
                                    </p>
                                )}
                            </div>
                        ) : (
                            /* ── Mobile preview ── */
                            <div className="flex flex-col items-center gap-6 py-4">

                                {/* Mobile card preview — live or skeleton */}
                                {previewMode === 'live' && selectedProduct ? (
                                    <RealMobileCard product={selectedProduct} cardItems={activeItems} showBookmark={mobileShowBookmark} />
                                ) : previewMode === 'live' && !selectedProduct ? (
                                    <div className="py-8">{selectProductPrompt}</div>
                                ) : (
                                    /* Skeleton / config mode */
                                    <div className="flex items-start justify-center gap-3">

                                        {/* Ghost card left */}
                                        <div className="size-20 shrink-0 rounded-md border bg-background opacity-50" />

                                        {/* Active mobile card */}
                                        <div className="w-[140px] shrink-0 overflow-hidden rounded-md border bg-background shadow-sm">
                                            {/* Thumbnail area (square) with overlay drop zones */}
                                            <div className="relative w-full overflow-hidden bg-foreground/5 pb-[100%]">
                                                <div className="absolute inset-0" style={HATCH_STYLE} />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <ImageIcon className="size-6 text-foreground/10" />
                                                </div>

                                                {/* Corner drop zones — visible while dragging */}
                                                {activeId !== null && (() => {
                                                    const overlayItem = activeItems.find(f => f.overlay && f.overlay !== 'none');
                                                    const occupiedPositions = new Set(overlayItem ? [overlayItem.overlay!] : []);
                                                    const activeItemOverlay = activeId ? activeItems.find(f => f.key === activeId)?.overlay : undefined;
                                                    return OVERLAY_CORNERS.map(pos => {
                                                        if (activeItemOverlay === pos) return null;
                                                        return (
                                                            <OverlayCornerDropZone
                                                                key={pos}
                                                                pos={pos}
                                                                isOccupied={occupiedPositions.has(pos)}
                                                            />
                                                        );
                                                    });
                                                })()}

                                                {/* Bookmark icon preview */}
                                                {mobileShowBookmark && (
                                                    <span className="absolute end-1 top-1 z-[5]">
                                                        <Bookmark className="size-3.5 text-foreground/35" strokeWidth={2} />
                                                    </span>
                                                )}

                                                {/* Draggable overlay badge (one max, configurable position) */}
                                                {(() => {
                                                    const overlayItem = activeItems.find(f => f.overlay && f.overlay !== 'none');
                                                    if (!overlayItem) return null;
                                                    const def = allFields.find(f => f.key === overlayItem.key);
                                                    if (!def) return null;
                                                    return (
                                                        <DraggableOverlayBadge
                                                            item={overlayItem}
                                                            def={def}
                                                            onRemove={handleRemoveFromCard}
                                                            onSettings={(key, el) => setMobileOverlaySettings({ key, anchorEl: el })}
                                                        />
                                                    );
                                                })()}
                                                {/* Settings popover for mobile overlay badge */}
                                                {mobileOverlaySettings && (() => {
                                                    const settingsItem = activeItems.find(f => f.key === mobileOverlaySettings.key);
                                                    if (!settingsItem) return null;
                                                    return (
                                                        <FieldSettingsPopover
                                                            anchorEl={mobileOverlaySettings.anchorEl}
                                                            onClose={() => setMobileOverlaySettings(null)}
                                                            config={settingsItem}
                                                            onUpdate={updates => handleUpdateField(settingsItem.key, updates)}
                                                        />
                                                    );
                                                })()}
                                            </div>

                                            {/* Text fields below thumbnail */}
                                            {textCardItems.length > 0 && (
                                                <div className="p-1.5">
                                                    {textCardItems.map((item) => {
                                                        const def = allFields.find(f => f.key === item.key);
                                                        if (!def) return null;
                                                        const style = item.style ?? (item.key === 'name' ? 'bold' : 'default');
                                                        return (
                                                            <div key={item.key} className="mb-px">
                                                                {item.concatWithPrevious ? null : (
                                                                    style === 'tag' || style === 'tag-outline' ? (
                                                                        <span
                                                                            className={cn(
                                                                                'inline-flex px-0.5 py-px',
                                                                                style === 'tag' && !item.badgeBgColor && 'bg-foreground/10',
                                                                                style === 'tag-outline' && cn('border', !item.badgeBgColor && 'border-foreground/30'),
                                                                            )}
                                                                            style={item.badgeBgColor ? (style === 'tag' ? { backgroundColor: item.badgeBgColor } : { borderColor: item.badgeBgColor }) : undefined}
                                                                        >
                                                                            <span
                                                                                className={cn('text-[9px] font-medium leading-tight', !item.badgeTextColor && 'text-foreground')}
                                                                                style={item.badgeTextColor ? { color: item.badgeTextColor } : undefined}
                                                                            >{def.label}</span>
                                                                        </span>
                                                                    ) : (
                                                                        <span className={cn(
                                                                            'block truncate text-[9px] leading-tight',
                                                                            style === 'bold' && 'font-medium',
                                                                            style === 'muted' ? 'text-foreground/30' : 'text-muted-foreground',
                                                                        )}>
                                                                            {def.label}
                                                                        </span>
                                                                    )
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* Ghost card right */}
                                        <div className="size-20 shrink-0 rounded-md border bg-background opacity-50" />
                                    </div>
                                )}

                                {/* Field config area (sortable list, same as web) */}
                                <div className="w-full max-w-[360px] rounded-lg border bg-background p-3">
                                    <p className="mb-2.5 text-xs text-muted-foreground">
                                        {t('PAGES.PRODUCT_DISPLAY.CARD_PREVIEW_SUBTITLE')}
                                    </p>
                                    {isLoading ? fieldListSkeleton(2) : renderFieldList()}
                                </div>
                            </div>
                        )}
                    </div>
                </CanvasPortal>

                <DragOverlay dropAnimation={null}>
                    {activeField ? <OverlayPill label={activeField.label} /> : null}
                </DragOverlay>
            </DndContext>
        </FeatureGate>
    );
};
